'use client';

import Image from 'next/image';
import { signIn } from 'next-auth/react';
import { SubscriptionBarrier } from '@stadionhq-v3/base-ui';
import { ArrowRightLight } from '@components/icons';
import crestIcon from '../../../app/icon.png';
import styles from './articleSubscriptionBarrier.module.scss';
import { ArticleSubscriptionBarrierProps } from './articleSubscriptionBarrier.types';

/**
 * Client wrapper around the design-system `SubscriptionBarrier` molecule used
 * to gate members-only articles for signed-out users.
 *
 * The two actions route to DIFFERENT FusionAuth hosted screens:
 *  - Primary CTA  → `signIn('register')` → the FA registration screen.
 *  - "Log in" link → `signIn('default')`  → the FA login screen.
 *
 * The molecule's `cta2` is a single string-only button, so the
 * "Already a member? Log in" line is rendered here as plain text + a separate
 * link (only "Log in" is interactive) rather than through `cta2`.
 *
 * NOTE: this only covers the logged-in/logged-out gate. The paid-member
 * ("subscriber") variant is planned and will extend this.
 */
const ArticleSubscriptionBarrier = ({
  title,
  message,
  registerLabel,
  alreadyAMemberLabel,
  signInLabel,
  classNames = [],
  testId = 'article-subscription-barrier',
}: ArticleSubscriptionBarrierProps) => {
  // Primary CTA → FusionAuth hosted registration screen.
  const handleRegister = () =>
    signIn('register', { callbackUrl: window.location.href });

  // Secondary link → FusionAuth hosted login screen.
  const handleSignIn = () =>
    signIn('default', { callbackUrl: window.location.href });

  return (
    <div className={styles.articleSubscriptionBarrier}>
      <SubscriptionBarrier
        testId={testId}
        classNames={classNames}
        crest={<Image src={crestIcon} alt="" width={80} height={80} priority />}
        title={title}
        message={message}
        size="medium"
        cta1={{
          text: registerLabel,
          variant: 'primary',
          iconAfter: <ArrowRightLight />,
          onClick: handleRegister,
        }}
      />
      <p className={styles.signInPrompt}>
        {alreadyAMemberLabel}{' '}
        <button
          type="button"
          className={styles.signInLink}
          onClick={handleSignIn}
          data-testid={`${testId}-sign-in`}
        >
          {signInLabel}
        </button>
      </p>
    </div>
  );
};

export default ArticleSubscriptionBarrier;
